import { Navigation } from '@toolpad/core';

import { Role, User, ADMIN_LIKE_ROLES } from 'types';

import { Config } from './env';
import { AppRoutes } from './routes';

interface Route {
  title: string;
  route: string;
  hidden?: boolean;
  hasParams?: boolean;
  available?: boolean;
  allowedRoles: Role[];
  alwaysAccessible?: boolean;
  canAccess?: (
    user?: User,
    options?: { selectedProjectId?: string | null }
  ) => boolean;
}

export const hasGameAnalyticsAccess = (
  user?: User,
  selectedProjectId?: string | null
) => {
  if (!Config.gameAnalyticsProjectId || !user) return false;

  const hasConfiguredProject =
    selectedProjectId == null ||
    selectedProjectId === Config.gameAnalyticsProjectId;
  if (!hasConfiguredProject) return false;

  switch (user.role) {
    case Role.ADMIN:
      return true;
    case Role.PROJECT_ADMIN:
    case Role.RESEARCH_ADMIN:
      return Boolean(user.projectIds?.includes(Config.gameAnalyticsProjectId));
    case Role.TENANT_ADMIN:
      return user.tenant?.projectId === Config.gameAnalyticsProjectId;
    default:
      return false;
  }
};

export const privateRoutes: Route[] = [
  {
    hasParams: true,
    title: 'Projects',
    route: AppRoutes.Project.Home,
    allowedRoles: ADMIN_LIKE_ROLES,
  },
  {
    hasParams: true,
    title: 'Organizations',
    route: AppRoutes.Tenant.Home,
    allowedRoles: ADMIN_LIKE_ROLES,
  },
  {
    title: 'Users',
    hasParams: true,
    route: AppRoutes.User.Home,
    allowedRoles: [Role.ADMIN, Role.PROJECT_ADMIN, Role.TENANT_ADMIN],
  },
  {
    hasParams: false,
    title: 'Users Logs',
    allowedRoles: ADMIN_LIKE_ROLES,
    route: AppRoutes.UsersLogs.Home,
  },
  {
    hasParams: true,
    title: 'Courses',
    route: AppRoutes.Courses.Home,
    allowedRoles: ADMIN_LIKE_ROLES,
  },
  {
    hasParams: true,
    title: 'Reports',
    route: AppRoutes.Reports.Home,
    allowedRoles: [
      Role.ADMIN,
      Role.PROJECT_ADMIN,
      Role.TENANT_ADMIN,
      Role.RESEARCH_ADMIN,
      Role.LHD_MIVAX_ADMIN,
    ],
  },
  {
    hidden: true,
    hasParams: true,
    title: 'Game Analytics',
    route: AppRoutes.Reports.GameAnalytics,
    canAccess: (user, options) =>
      hasGameAnalyticsAccess(user, options?.selectedProjectId),
    allowedRoles: [
      Role.ADMIN,
      Role.PROJECT_ADMIN,
      Role.TENANT_ADMIN,
      Role.RESEARCH_ADMIN,
    ],
  },
  {
    hasParams: true,
    title: 'Profile Forms',
    allowedRoles: ADMIN_LIKE_ROLES,
    route: AppRoutes.ProfileForms.Home,
    available: Config.profileFormsAvailable,
  },
  {
    hasParams: true,
    title: 'Library Structure',
    available: Config.mivaxAvailable,
    route: AppRoutes.Library.Tags.Home,
    allowedRoles: [
      Role.ADMIN,
      Role.PROJECT_ADMIN,
      Role.TENANT_ADMIN,
      Role.CONTENT_MANAGER,
      Role.LIBRARY_ADMIN,
    ],
  },
  {
    hasParams: true,
    title: 'Resources',
    available: Config.mivaxAvailable,
    route: AppRoutes.Library.Resources.Home,
    allowedRoles: [
      Role.ADMIN,
      Role.PROJECT_ADMIN,
      Role.TENANT_ADMIN,
      Role.CONTENT_MANAGER,
      Role.LIBRARY_ADMIN,
    ],
  },
  {
    hasParams: false,
    allowedRoles: [Role.ADMIN],
    title: 'Message Org Admins',
    route: AppRoutes.MessageOrgAdmins.Home,
  },
  {
    hasParams: false,
    title: 'PDF Management',
    route: AppRoutes.PdfManagement.Home,
    available: Config.pdfManagementAvailable,
    allowedRoles: [Role.ADMIN, Role.REPORT_MANAGER],
  },
  {
    title: 'Profile',
    hasParams: false,
    allowedRoles: [],
    alwaysAccessible: true,
    route: AppRoutes.Profile.Home,
  },
];

const normalizeRoute = (route: string): string => route.slice(1);

const createPattern = (segment: string, hasParams: boolean = true): string =>
  hasParams ? `${segment}{/:id}*` : segment;

export const resolvePrivateRoute = (
  route: Route,
  user?: User,
  libraryTenantId?: string,
  libraryEnabled?: boolean
) => {
  if (!route.route.includes(':tenantId')) {
    return route.route;
  }

  if (user?.role && ADMIN_LIKE_ROLES.includes(user.role)) {
    if (libraryTenantId) {
      return route.route.replace(':tenantId', libraryTenantId);
    }
    if (route.route === AppRoutes.Library.Tags.Home) {
      return AppRoutes.Library.Tags.Placeholder;
    }
    if (route.route === AppRoutes.Library.Resources.Home) {
      return AppRoutes.Library.Resources.Placeholder;
    }
    return AppRoutes.Library.Home;
  }

  if (
    (user?.role === Role.TENANT_ADMIN ||
      user?.role === Role.CONTENT_MANAGER ||
      user?.role === Role.LIBRARY_ADMIN) &&
    libraryEnabled &&
    user?.tenantId
  ) {
    return route.route.replace(':tenantId', user.tenantId);
  }

  return AppRoutes.Library.Home;
};

export const getNavigation = (
  user?: User,
  options?: {
    libraryTenantId?: string;
    libraryEnabled?: boolean;
    selectedProjectId?: string | null;
  }
): Navigation => {
  const role = user?.role;
  const accessibleRoutes = privateRoutes.filter((route) => {
    if (route.hidden) {
      return false;
    }
    if (route.alwaysAccessible) {
      return true;
    }
    if (route.available === false) {
      return false;
    }
    if (route.canAccess && !route.canAccess(user, options)) {
      return false;
    }
    if (role) {
      return route.allowedRoles.includes(role);
    }
    return false;
  });

  return accessibleRoutes
    .filter((route) => {
      const isLibraryRoute = route.route.startsWith(
        `${AppRoutes.Library.Home}/`
      );
      const isTenantScopedRole =
        user?.role === Role.TENANT_ADMIN ||
        user?.role === Role.CONTENT_MANAGER ||
        user?.role === Role.LIBRARY_ADMIN;
      if (isLibraryRoute && isTenantScopedRole && !options?.libraryEnabled) {
        return false;
      }
      return true;
    })
    .map((route) => {
      const resolvedRoute = resolvePrivateRoute(
        route,
        user,
        options?.libraryTenantId,
        options?.libraryEnabled
      );
      const segment = normalizeRoute(resolvedRoute);
      const adminWithoutLibraryTenant =
        user?.role &&
        ADMIN_LIKE_ROLES.includes(user.role) &&
        !options?.libraryTenantId;
      const patternSource =
        route.route.includes(':tenantId') && !adminWithoutLibraryTenant
          ? normalizeRoute(route.route)
          : segment;
      const shouldHighlightLibraryRoot =
        route.route === AppRoutes.Library.Tags.Home &&
        adminWithoutLibraryTenant;
      return {
        segment,
        title: route.title,
        route: resolvedRoute,
        pattern: shouldHighlightLibraryRoot
          ? createPattern(normalizeRoute(AppRoutes.Library.Home), true)
          : createPattern(patternSource, route.hasParams),
      };
    });
};
